import client from './client';

const baseUrl = '/api/v1/cadquote';

// 도면 견적(시범) - STEP(+선택 DXF) 업로드해 cad-service 분석 결과를 받아온다.
// client.js 인터셉터가 FormData면 Content-Type을 손대지 않으므로(멀티파트 boundary
// 자동 설정) 여기서는 그냥 FormData만 만들어 넘긴다.
// splitOptions({splitAt, splitAxis})를 주면 "레이어로 나누기" 재분석 요청이 된다 -
// splitAt은 숫자 배열을 그대로 JSON 문자열로 보낸다(백엔드가 그 형식을 기대함).
export const analyzeCadFile = (file, drawingFile, onProgress, splitOptions) => {
  const formData = new FormData();
  formData.append('file', file);
  if (drawingFile) {
    formData.append('drawing', drawingFile);
  }
  if (splitOptions?.splitAt?.length) {
    formData.append('splitAt', JSON.stringify(splitOptions.splitAt));
    if (splitOptions.splitAxis) {
      formData.append('splitAxis', splitOptions.splitAxis);
    }
  }
  return client.post(`${baseUrl}/analyze`, formData, {
    onUploadProgress: onProgress
      ? (evt) => {
        if (evt.total) onProgress(Math.round((evt.loaded * 100) / evt.total));
      }
      : undefined,
  });
};

// 응답 봉투가 {result, info} 로 내려온다 - eqpAgent.js의 extract* 헬퍼들과 같은 자리.
export const extractAnalyzeInfo = (res) => res?.data?.info ?? null;

// ================= 회사 단가표 =================

/** 회사 재질 단가표 + 공통 기본값(시간당 가공비 등)을 받아온다 - 페이지 로드 시 기본값으로 쓴다. */
export const getCadQuotePrices = () => client.get(`${baseUrl}/prices`);

/**
 * 단가표 저장(upsert). materials는 MATERIALS 형태({코드: {density, pricePerKg, roughRate,
 * finishRate}})를 배열로 바꿔 보낸다, setting은 {hourlyRate, setupMin, stockMarginMm, marginRate}.
 * 지금은 로그인한 누구나 저장할 수 있다(시범 단계, 백엔드 CadQuotePriceService 주석 참고).
 */
export const saveCadQuotePrices = (materials, setting) => (
  client.put(`${baseUrl}/prices`, { materials, setting })
);

// ================= 저장된 견적 =================

/**
 * 견적 저장(STEP/DXF 파일 + payload). payload = {partNo, analysis(mesh/previewSvg 제외),
 * form, cost, amounts: {supplyAmt, vatAmt, totalAmt}, memo} - analysis는 호출 전에
 * cadQuoteCalc.stripAnalysisForSave로 걷어낸 걸 넘겨야 한다(여기서는 그대로 JSON.stringify만 함).
 */
export const saveCadQuote = (file, drawingFile, payload) => {
  const formData = new FormData();
  formData.append('file', file);
  if (drawingFile) {
    formData.append('drawing', drawingFile);
  }
  formData.append('payload', JSON.stringify(payload));
  return client.post(`${baseUrl}/quotes`, formData);
};

/** 저장된 견적 목록(키워드: 품번/파일명). page는 0부터 시작. */
export const listCadQuotes = ({ page = 0, size = 20, keyword } = {}) => (
  client.get(`${baseUrl}/quotes`, { params: { page, size, keyword: keyword || undefined } })
);

/** 저장된 견적 1건 상세(analysis/form/cost는 파싱된 JSON 객체로 내려온다). */
export const getCadQuote = (qtUuid) => client.get(`${baseUrl}/quotes/${qtUuid}`);

/** 실제 견적가/비고 입력. */
export const patchCadQuote = (qtUuid, finalAmt, memo) => (
  client.patch(`${baseUrl}/quotes/${qtUuid}`, { finalAmt, memo })
);

/** 소프트 삭제. */
export const deleteCadQuote = (qtUuid) => client.delete(`${baseUrl}/quotes/${qtUuid}`);

/** 저장된 원본 STEP 파일 다운로드("STEP 받기" 버튼). */
export const downloadCadQuoteFile = (qtUuid) => (
  client.get(`${baseUrl}/quotes/${qtUuid}/file`, { responseType: 'blob' })
);

/**
 * 비슷한 과거 견적 찾기. features는 cadQuoteCalc.featuresFromAnalysis(analysis, form) 결과
 * ({bbox:{x,y,z}, volumeCm3, areaCm2, partCnt, layerCnt})를 그대로 넘기면 된다.
 */
export const findSimilarCadQuotes = (features) => client.post(`${baseUrl}/quotes/similar`, features);
